// ════════════════════════════════════════════════════════════════════
// TERROR — StarterArt  (THE DECK BOXES)
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-08-22: *"need pictures where we can — starter decks have
// none — need it absolutely professional Illegal Empire AAA quality."*
//
// He was right, and the gap was embarrassing once you saw it: the
// Packs tab of the shop renders a foil wrapper per pack (PackArt), and
// the Starters tab beside it rendered A NINE-PIXEL COLOURED DOT. Same
// counter, same money, one shelf with art and one shelf with
// punctuation. Onboarding was worse — a player's FIRST choice in the
// entire game, twelve decks, and nothing to look at while choosing.
//
// ── WHY THIS IS NOT PackArt WITH A DIFFERENT COLOUR ─────────────────
// A booster pack is a SEALED WRAPPER: you cannot see in, so its art is
// a crimp line, a sigil and a sheen, and its whole job is "there is
// something in here." A starter deck is the opposite object — it is an
// OPEN, KNOWN, FINISHED THING, forty cards someone already chose for
// you. So it is drawn as a DECK: a stack with real edges behind a
// faced top card, banded, crested and plated. You can tell the two
// shelves apart from across the room, which is the point.
//
// Sharing PackArt's generator would also have broken the standing law
// the holo tiers live under — never a recolour, never a generic. A
// wrapper tinted twelve ways is exactly a recolour.
//
// ── ZERO ASSETS, ON PURPOSE ─────────────────────────────────────────
// Every mark here is inline SVG. No CDN fetch, no 404, no art to
// commission before the shelf can ship, and it scales from a 34px row
// bullet to a 200px shop tile off one `w`/`h`. If caP'n later paints
// twelve deck boxes by hand, swap the <StarterArt> call for an <img>
// and nothing else in the app has to know.
//
// ── THREE THINGS ARE READABLE AT THREE DISTANCES ────────────────────
//   far   — the ILK COLLAR and the body colour: which of the four
//           families this is, from a grid.
//   mid   — the HOUSE CREST: which of the twelve lineages, one glance.
//   close — the code plate and the milled edge: the object itself.
// That ladder is the whole layout brief. A tile that only works at one
// size is a tile that will look wrong on the other two surfaces.
//
// ── THE TWELVE CRESTS ARE TWELVE DRAWINGS ───────────────────────────
// Not one shape rotated twelve ways. H-01 is a giant with a horizon
// through it; H-09 is a dense core with polar jets; Xulan is a hooded
// scalpel; the Spectral Tiger is a claw through a broken ring; the
// Shadow Fox is ears and a muzzle carved out of the dark; the Inverted
// Serpent eats its own tail the wrong way round; Z4Z is a sword
// through a gear; DG-0 is a wing over a shield; the Antdroid is a hive
// cell with mandibles; the Lucky Dragon is a coin with an eye in it;
// the Premier Dragon is a crowned claw; the Chaos Dragon is a scale
// bursting apart. Adding a thirteenth deck = one crest here keyed by
// its `id`, and the shelf, onboarding and the Plaza all learn it at
// once. Nothing falls over if you forget — an unknown id draws the
// house sigil, deterministically seeded off the deck id so two new
// decks never look identical.
//
// ── USAGE ───────────────────────────────────────────────────────────
//   <StarterArt deck={deck} w={104} h={146} />          // a shelf tile
//   <StarterArt deck={deck} w={34} h={48} plain />      // a list bullet
//   <StarterArt deck={deck} w={200} h={280} foil />     // the hero
// `deck` is a STARTER_DECKS row — it needs `id`, `color`, `ilk`.
// ════════════════════════════════════════════════════════════════════

import React from "react";
import { cardUrl } from "./CDN";   // ⭐ card art over invention — see THE LORD'S FACE below

// ── deterministic, so a deck's flecks never re-roll between renders ──
// Same tiny PRNG shape PackArt uses. It is seeded off the deck id
// alone, which means the art is stable across sessions, across
// surfaces, and across a hot reload — a tile that shimmers differently
// every mount reads as a bug even when it is "just" decoration.
const seededRand = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return () => { h ^= h << 13; h ^= h >>> 17; h ^= h << 5; return ((h >>> 0) % 10000) / 10000; };
};

// Lighten/darken a hex by amt (-1..1). One accent colour in, a whole
// slab palette out — the deck rows only carry `color`.
const shade = (hex, amt) => {
  const n = parseInt(String(hex || "#888888").replace("#", ""), 16);
  const cl = (v) => Math.max(0, Math.min(255, Math.round(v)));
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const f = (v) => (amt >= 0 ? v + (255 - v) * amt : v * (1 + amt));
  return `#${((1 << 24) + (cl(f(r)) << 16) + (cl(f(g)) << 8) + cl(f(b))).toString(16).slice(1)}`;
};

// ── THE ILK COLLAR — the far-distance read ───────────────────────────
// Four rings, one per Ilk, drawn behind the crest. Deliberately NOT
// four colours of the same ring: Space orbits, Phantom breaks and
// drifts, Time ticks, Dragon bites. You should be able to sort a grid
// of twelve into four families without reading a single word.
const ILK_COLLAR = {
  Space: (c) => `
    <ellipse cx="0" cy="9" rx="39" ry="13" stroke="${c}" stroke-width="1.2" fill="none" opacity="0.3" transform="rotate(-15)"/>
    <circle cx="36" cy="-1" r="2.1" fill="${c}" opacity="0.5"/>
    <g fill="${c}" opacity="0.4">
      <circle cx="-33" cy="-24" r="1.2"/><circle cx="30" cy="24" r="1.5"/>
      <circle cx="-24" cy="30" r="1.1"/><circle cx="22" cy="-30" r="1.3"/>
    </g>`,
  Phantom: (c) => `
    <circle cx="0" cy="0" r="37" stroke="${c}" stroke-width="1.2" fill="none" opacity="0.3" stroke-dasharray="15 10"/>
    <path d="M-43 -13 Q-49 0 -43 13" stroke="${c}" stroke-width="1" fill="none" opacity="0.3"/>
    <path d="M43 -13 Q49 0 43 13" stroke="${c}" stroke-width="1" fill="none" opacity="0.3"/>`,
  Time: (c) => `
    <circle cx="0" cy="0" r="37" stroke="${c}" stroke-width="1.1" fill="none" opacity="0.3"/>
    <g stroke="${c}" stroke-width="1.4" opacity="0.34">
      <path d="M0 -37 L0 -32"/><path d="M0 37 L0 32"/><path d="M-37 0 L-32 0"/><path d="M37 0 L32 0"/>
      <path d="M26 -26 L22.5 -22.5"/><path d="M-26 26 L-22.5 22.5"/>
      <path d="M26 26 L22.5 22.5"/><path d="M-26 -26 L-22.5 -22.5"/>
    </g>`,
  Dragon: (c) => `
    <path d="M0 -38 A38 38 0 1 1 -0.1 -38" stroke="${c}" stroke-width="1.3" fill="none" opacity="0.32" stroke-dasharray="58 16"/>
    <path d="M-10 -39 L0 -30 L10 -39" stroke="${c}" stroke-width="1.5" fill="none" opacity="0.42"/>
    <path d="M-10 39 L0 30 L10 39" stroke="${c}" stroke-width="1.5" fill="none" opacity="0.42"/>`,
};

// ── THE TWELVE HOUSE CRESTS ──────────────────────────────────────────
// Each drawn centred on 0,0 in a roughly 44-unit box, stroke-first so
// they hold at 34px and do not turn to mud. `c` is the deck accent,
// `l` its light. Keyed by STARTER_DECKS id.
const CRESTS = {
  // H-01 BLUE GIANT — the giant, with a horizon cut through it. They
  // "choose words as their first resort": a whole star, and a line
  // drawn across it rather than through it.
  starter_h01: (c, l) => `
    <circle cx="0" cy="0" r="17" stroke="${c}" stroke-width="2.8" fill="none"/>
    <circle cx="0" cy="0" r="7" fill="${l}" opacity="0.9"/>
    <path d="M-17 -3 L17 -3" stroke="${l}" stroke-width="2.2" opacity="0.95"/>
    <path d="M-14 5 L14 5" stroke="${l}" stroke-width="1.7" opacity="0.7"/>
    <g stroke="${l}" stroke-width="2" stroke-linecap="round" opacity="0.85">
      <path d="M0 -23 L0 -30"/><path d="M0 23 L0 30"/>
      <path d="M-23 0 L-30 0"/><path d="M23 0 L30 0"/>
      <path d="M-16.5 -16.5 L-21 -21"/><path d="M16.5 16.5 L21 21"/>
      <path d="M16.5 -16.5 L21 -21"/><path d="M-16.5 16.5 L-21 21"/>
    </g>`,

  // H-09 NEUTRON STAR — militancy first. A dense core, collapsed, with
  // two polar jets. Nothing decorative on it at all.
  starter_h09: (c, l) => `
    <circle cx="0" cy="0" r="9" fill="${l}"/>
    <circle cx="0" cy="0" r="15" stroke="${c}" stroke-width="2.4" fill="none"/>
    <ellipse cx="0" cy="0" rx="27" ry="8" stroke="${c}" stroke-width="1.8" fill="none" opacity="0.75"/>
    <path d="M0 -18 L0 -27" stroke="${l}" stroke-width="3.4" stroke-linecap="round"/>
    <path d="M0 18 L0 27" stroke="${l}" stroke-width="3.4" stroke-linecap="round"/>
    <path d="M-7 -22 L0 -32 L7 -22" stroke="${l}" stroke-width="2.2" fill="none" stroke-linejoin="round"/>
    <path d="M-7 22 L0 32 L7 22" stroke="${l}" stroke-width="2.2" fill="none" stroke-linejoin="round"/>`,

  // XULAN — Wanderer, Surgeon, Shadow. A hood, and a scalpel through
  // it. Diamond skin is the facet; the blade is what they are for.
  starter_xulan: (c, l) => `
    <path d="M0 -26 Q19 -14 17 8 Q10 22 0 25 Q-10 22 -17 8 Q-19 -14 0 -26 Z"
      stroke="${c}" stroke-width="2.9" fill="none" stroke-linejoin="round"/>
    <path d="M-11 -8 Q0 -14 11 -8" stroke="${c}" stroke-width="2" fill="none" opacity="0.8"/>
    <circle cx="-6" cy="-1" r="2.4" fill="${l}"/>
    <circle cx="6" cy="-1" r="2.4" fill="${l}"/>
    <path d="M-16 24 L20 -12" stroke="${l}" stroke-width="2.4" stroke-linecap="round"/>
    <path d="M16 -17 L24 -9" stroke="${c}" stroke-width="3" stroke-linecap="round"/>`,

  // SPECTRAL TIGER — a claw through a broken ring. Haunting Presence:
  // the ring is what it stepped out of and did not close behind it.
  starter_spectral_tiger: (c, l) => `
    <path d="M2 -22 A22 22 0 1 1 -16 15" stroke="${c}" stroke-width="3.2" fill="none"/>
    <g stroke="${l}" stroke-width="3.4" stroke-linecap="round" fill="none">
      <path d="M-17 -17 Q-3 -3 7 15"/>
      <path d="M-7 -21 Q5 -7 15 11"/>
      <path d="M3 -23 Q13 -11 21 5"/>
    </g>
    <circle cx="-17" cy="16" r="3" fill="${c}"/>
    <circle cx="4" cy="-24" r="2.4" fill="${c}"/>`,

  // SHADOW FOX — ears and a muzzle carved OUT of the dark rather than
  // drawn on it. Lucky Shadow: you see the shape by what is missing.
  starter_shadow_fox: (c, l) => `
    <path d="M-18 -16 L-11 2 L0 10 L11 2 L18 -16 L8 -9 Q0 -12 -8 -9 Z"
      stroke="${c}" stroke-width="2.4" fill="none" stroke-linejoin="round"/>
    <path d="M-11 2 L0 20 L11 2" stroke="${l}" stroke-width="2" fill="none" stroke-linejoin="round"/>
    <circle cx="-6" cy="-3" r="2.4" fill="${l}"/>
    <circle cx="6" cy="-3" r="2.4" fill="${l}"/>
    <path d="M-24 12 Q-16 20 -6 22" stroke="${c}" stroke-width="1.6" fill="none" opacity="0.6"/>
    <path d="M24 12 Q16 20 6 22" stroke="${c}" stroke-width="1.6" fill="none" opacity="0.6"/>`,
  // INVERTED SERPENT — an ouroboros that has gone round the wrong way,
  // so the head meets the tail from OUTSIDE the loop. Inverted Scales.
  starter_inverted_serpent: (c, l) => `
    <path d="M-13 -20 Q10 -18 6 -4 Q2 6 -6 8 Q-14 10 -12 18 Q-10 25 4 22"
      stroke="${c}" stroke-width="3.2" fill="none" stroke-linecap="round"/>
    <path d="M-13 -20 L-22 -26 L-19 -15 Z" stroke="${l}" stroke-width="1.8" fill="none" stroke-linejoin="round"/>
    <circle cx="-16" cy="-21" r="1.8" fill="${l}"/>
    <g stroke="${l}" stroke-width="1.6" opacity="0.9">
      <path d="M4 22 L12 26"/><path d="M4 22 L12 20"/><path d="M4 22 L11 14"/>
    </g>
    <g stroke="${l}" stroke-width="1.5" opacity="0.75">
      <path d="M-2 -13 L4 -16"/><path d="M-4 -4 L2 -7"/><path d="M-11 4 L-6 1"/>
    </g>`,

  // Z4Z — Demon, Wizard, H'ehll's Sword. The sword, through the gear
  // that keeps bringing it back around.
  starter_z4z: (c, l) => `
    <circle cx="0" cy="2" r="14" stroke="${c}" stroke-width="2.2" fill="none"/>
    <g stroke="${c}" stroke-width="2.4" opacity="0.9">
      <path d="M0 -16 L0 -12"/><path d="M0 20 L0 16"/><path d="M-18 2 L-14 2"/><path d="M18 2 L14 2"/>
      <path d="M-12.5 -10.5 L-10 -8"/><path d="M12.5 -10.5 L10 -8"/>
    </g>
    <path d="M0 -24 L0 22" stroke="${l}" stroke-width="2.6" stroke-linecap="round"/>
    <path d="M-9 -12 L9 -12" stroke="${l}" stroke-width="2.4" stroke-linecap="round"/>
    <path d="M-4 -24 L0 -30 L4 -24" stroke="${l}" stroke-width="2" fill="none"/>`,
  // DG-0 — Angel, Farmer, I'nfini's Shield. One wing over the shield.
  // Justice Reigns, and it is a defensive posture.
  starter_dg0: (c, l) => `
    <path d="M0 -18 L15 -12 L15 6 Q15 18 0 25 Q-15 18 -15 6 L-15 -12 Z"
      stroke="${c}" stroke-width="2.6" fill="none" stroke-linejoin="round"/>
    <path d="M0 -8 L0 15" stroke="${l}" stroke-width="2.2" opacity="0.9"/>
    <path d="M-8 -1 L8 -1" stroke="${l}" stroke-width="2.2" opacity="0.9"/>
    <path d="M-15 -12 L-24 -18 L-21 -10 L-30 -14 L-26 -5 L-15 -2"
      stroke="${l}" stroke-width="1.9" fill="none" stroke-linejoin="round"/>
    <path d="M15 -12 L24 -18 L21 -10 L30 -14 L26 -5 L15 -2"
      stroke="${l}" stroke-width="1.9" fill="none" stroke-linejoin="round"/>`,

  // ANTDROID — the Colonel's own elite. A hive cell, and mandibles.
  // King of the Hill is not a metaphor about a hill.
  starter_antdroid: (c, l) => `
    <path d="M0 -20 L17 -10 L17 10 L0 20 L-17 10 L-17 -10 Z"
      stroke="${c}" stroke-width="2.6" fill="none" stroke-linejoin="round"/>
    <path d="M0 -10 L8.5 -5 L8.5 5 L0 10 L-8.5 5 L-8.5 -5 Z"
      stroke="${l}" stroke-width="1.9" fill="none" stroke-linejoin="round"/>
    <circle cx="0" cy="0" r="2.6" fill="${l}"/>
    <path d="M-8.5 -15 Q-19 -24 -10 -29" stroke="${l}" stroke-width="2.4" fill="none" stroke-linecap="round"/>
    <path d="M8.5 -15 Q19 -24 10 -29" stroke="${l}" stroke-width="2.4" fill="none" stroke-linecap="round"/>
    <path d="M-17 4 L-25 8" stroke="${c}" stroke-width="2" stroke-linecap="round" opacity="0.8"/>
    <path d="M17 4 L25 8" stroke="${c}" stroke-width="2" stroke-linecap="round" opacity="0.8"/>`,

  // LUCKY DRAGON — a coin with an eye in it. Dragon Eyes: the luck is
  // the animal, and it is looking back at you.
  starter_lucky_dragon: (c, l) => `
    <circle cx="0" cy="0" r="20" stroke="${c}" stroke-width="2.6" fill="none"/>
    <circle cx="0" cy="0" r="26" stroke="${c}" stroke-width="1.2" fill="none" opacity="0.5"/>
    <path d="M-14 0 Q0 -12 14 0 Q0 12 -14 0 Z" stroke="${l}" stroke-width="2.2" fill="none"/>
    <ellipse cx="0" cy="0" rx="3" ry="7" fill="${l}"/>
    <g stroke="${l}" stroke-width="1.4" opacity="0.7">
      <path d="M-22 -14 L-17 -10"/><path d="M22 -14 L17 -10"/>
      <path d="M-22 14 L-17 10"/><path d="M22 14 L17 10"/>
    </g>`,
  // PREMIER DRAGON — a crowned claw. Show Stomper, and the crown is on
  // the foot rather than the head, which is the joke and the threat.
  starter_premier_dragon: (c, l) => `
    <path d="M-20 10 Q-20 -6 -6 -12 Q10 -18 22 -6 Q26 0 19 4 L9 7 L15 15 L1 13 Q-8 20 -15 16 Q-20 13 -20 10 Z"
      stroke="${c}" stroke-width="2.8" fill="none" stroke-linejoin="round"/>
    <path d="M-6 -12 L-16 -24 L-3 -19" stroke="${c}" stroke-width="2.2" fill="none" stroke-linejoin="round"/>
    <path d="M9 7 L12 12 M15 15 L19 10" stroke="${l}" stroke-width="1.7" opacity="0.9"/>
    <circle cx="8" cy="-4" r="2.9" fill="${l}"/>
    <path d="M-2 -14 L2 -25 L9 -19 L15 -28 L20 -19 L27 -22"
      stroke="${l}" stroke-width="2.2" fill="none" stroke-linejoin="round"/>`,

  // CHAOS DRAGON — a scale coming apart. I or None: the shape is still
  // legible as one thing, and it is already in six pieces.
  starter_chaos_dragon: (c, l) => `
    <path d="M-4 -23 Q13 -13 12 4 Q11 18 -4 24" stroke="${c}" stroke-width="2.8" fill="none" stroke-linecap="round"/>
    <path d="M-6 -21 Q-19 -11 -18 4 Q-17 17 -6 22" stroke="${c}" stroke-width="2.8" fill="none" stroke-linecap="round"/>
    <path d="M-1 -22 L-9 -4 L2 -1 L-7 20" stroke="${l}" stroke-width="3" fill="none" stroke-linejoin="round" stroke-linecap="round"/>
    <path d="M18 -12 L26 -18 L22 -6 Z" stroke="${c}" stroke-width="1.8" fill="none" stroke-linejoin="round" opacity="0.9"/>
    <path d="M-22 12 L-30 17 L-25 22 Z" stroke="${c}" stroke-width="1.8" fill="none" stroke-linejoin="round" opacity="0.9"/>
    <circle cx="24" cy="14" r="2" fill="${c}" opacity="0.8"/>
    <circle cx="-26" cy="-9" r="1.6" fill="${c}" opacity="0.7"/>`,
};

// The fallback: THE HOUSE MARK, seeded off the deck id so a deck with
// no crest yet still gets a stable, distinct one instead of a blank
// medallion. Never the right answer long-term — add the crest — but a
// missing entry must not be a visual bug.
const houseMark = (c, l, seed) => {
  const r = seededRand(seed);
  const pts = 5 + Math.floor(r() * 4);
  const arms = Array.from({ length: pts }, (_, i) => {
    const a = (i / pts) * Math.PI * 2 - Math.PI / 2;
    const R = 12 + r() * 10;
    return `M0 0 L${(Math.cos(a) * R).toFixed(1)} ${(Math.sin(a) * R).toFixed(1)}`;
  }).join(" ");
  return `
    <circle cx="0" cy="0" r="20" stroke="${c}" stroke-width="2.2" fill="none" opacity="0.85"/>
    <path d="${arms}" stroke="${l}" stroke-width="2" fill="none" stroke-linecap="round"/>
    <circle cx="0" cy="0" r="4" fill="${l}"/>`;
};

// A short plate code from the deck id: starter_spectral_tiger → "ST".
// Two or three letters, derived, so a new deck needs no new field.
export const starterCode = (id) => {
  const parts = String(id || "").replace(/^starter_/, "").split("_").filter(Boolean);
  if (!parts.length) return "TER";
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  return parts.map((p) => p[0]).join("").slice(0, 3).toUpperCase();
};

// ── THE LORD'S FACE ──────────────────────────────────────────────────
// ⭐ caP'n, 2026-08-22: *"starter decks in the shop should use the Lord
// being of their respective deck. Always use card art when possible —
// let's work smarter not harder."*
//
// He is right and it is the cheaper answer twice over: the art already
// exists on the CDN, it is the real card, and a player who has seen
// H-01 Super Nova on a deck box recognises him the moment he turns up
// in a duel. Twelve hand-drawn crests cannot buy that.
//
// So the crest is no longer the face of the box — it is the FALLBACK
// UNDER it. The layering is deliberate: glow → collar → crest → art →
// scrim → belt → plate → edge. If the art loads it covers the crest,
// which is correct, art wins. If it 404s, an SVG <image> simply draws
// nothing and the crest is what you see, with no JS, no onError, and
// no broken-image box. That is the same graceful failure the Foil
// Vault tiles have, achieved with one element instead of a handler.
//
// The crest table stays and is still worth its lines: it is what the
// box shows before the art exists, and it is the only thing that works
// if the CDN is down.
const LEVEL3 = /^starter_/;
export const lordIdFor = (deckId) => String(deckId || "").replace(LEVEL3, "") + "_lord";

// ════════════════════════════════════════════════════════════════════
// <StarterArt> — the deck box
// ════════════════════════════════════════════════════════════════════
//   deck   a STARTER_DECKS row ({ id, color, ilk }). Required.
//   w / h  px. Everything inside scales off the 120×168 viewBox.
//   foil   adds the travelling sheen — for a hero/selected tile only.
//   plain  drops the stack shadow and flecks: for a small list bullet,
//          where those layers turn to noise rather than depth.
//   dim    0..1 multiplier for an unaffordable / unselected tile.
export default function StarterArt({
  deck,
  w = 104,
  h = 146,
  foil = false,
  plain = false,
  dim = 1,
  style = {},
  className = "",
}) {
  const id = deck?.id || "starter_unknown";
  const accent = deck?.color || "#c8963c";
  const ilk = deck?.ilk || "Space";

  const rand = seededRand(id);
  const light = shade(accent, 0.55);
  const mid = shade(accent, -0.15);
  const dark = shade(accent, -0.66);
  const darker = shade(accent, -0.84);

  const crest = (CRESTS[id] || ((c, l) => houseMark(c, l, id)))(accent, light);
  const collar = (ILK_COLLAR[ilk] || ILK_COLLAR.Space)(light);
  const code = starterCode(id);
  const lordId = lordIdFor(id);

  // Flecks: seeded once per deck, never re-rolled. Kept off `plain`.
  const flecks = plain ? [] : Array.from({ length: 9 }, (_, i) => ({
    x: 12 + rand() * 96, y: 12 + rand() * 144, r: 0.5 + rand() * 1.1,
    d: (rand() * 4).toFixed(2), k: i,
  }));

  const uid = React.useId().replace(/:/g, "");

  return (
    <svg
      className={className}
      width={w}
      height={h}
      viewBox="0 0 120 168"
      style={{
        display: "block",
        opacity: dim,
        filter: plain ? "none" : `drop-shadow(0 4px 12px ${darker}cc)`,
        ...style,
      }}
      role="img"
      aria-label={deck?.name ? `${deck.name} deck` : "Starter deck"}
    >
      <defs>
        <linearGradient id={`sa-body-${uid}`} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0%" stopColor={mid} />
          <stop offset="46%" stopColor={dark} />
          <stop offset="100%" stopColor={darker} />
        </linearGradient>
        <linearGradient id={`sa-edge-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={light} stopOpacity="0.9" />
          <stop offset="55%" stopColor={accent} stopOpacity="0.35" />
          <stop offset="100%" stopColor={darker} stopOpacity="0.9" />
        </linearGradient>
        <radialGradient id={`sa-glow-${uid}`} cx="50%" cy="38%" r="52%">
          <stop offset="0%" stopColor={light} stopOpacity="0.34" />
          <stop offset="70%" stopColor={accent} stopOpacity="0.08" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`sa-sheen-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="50%" stopColor="#ffffff" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`sa-scrim-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={darker} stopOpacity="0.55" />
          <stop offset="38%"  stopColor={darker} stopOpacity="0.12" />
          <stop offset="100%" stopColor={darker} stopOpacity="0.9" />
        </linearGradient>
        <clipPath id={`sa-clip-${uid}`}>
          <rect x="8" y="6" width="98" height="154" rx="5" />
        </clipPath>
      </defs>

      {/* ── THE STACK. Three card edges behind the face, offset down and
          right. This is the one thing that makes it read as a DECK
          rather than a card, and it costs three rects. */}
      {!plain && (
        <g opacity="0.95">
          <rect x="14" y="12" width="98" height="154" rx="5" fill={darker} stroke={dark} strokeWidth="0.8" />
          <rect x="12" y="10" width="98" height="154" rx="5" fill={shade(accent, -0.78)} stroke={dark} strokeWidth="0.8" />
          <rect x="10" y="8" width="98" height="154" rx="5" fill={shade(accent, -0.72)} stroke={dark} strokeWidth="0.8" />
        </g>
      )}

      {/* ── THE FACE ── */}
      <rect x="8" y="6" width="98" height="154" rx="5" fill={`url(#sa-body-${uid})`} stroke={accent} strokeWidth="1.1" />

      <g clipPath={`url(#sa-clip-${uid})`}>
        <rect x="8" y="6" width="98" height="154" fill={`url(#sa-glow-${uid})`} />

        {/* Deterministic flecks — dust in the varnish, not sparkle. */}
        {flecks.map((f) => (
          <circle key={f.k} cx={f.x} cy={f.y} r={f.r} fill={light} opacity="0.42">
            <animate attributeName="opacity" values="0.12;0.6;0.12" dur="3.6s" begin={`${f.d}s`} repeatCount="indefinite" />
          </circle>
        ))}

        {/* ── THE CREST, under its Ilk collar ──
            The collar is BACKGROUND: pushed out to r≈38 and held near
            0.3 opacity, because at r≈29 it was louder than the crest
            and every Space deck read as the same orbit diagram. On a
            `plain` bullet it is dropped entirely and the crest scales
            up — at 34px there is no room for two rings of information,
            and the one that has to survive is which deck this is. */}
        <g transform={`translate(57 ${plain ? 68 : 62}) scale(${plain ? 1.5 : 1.16})`}
           style={{ filter: `drop-shadow(0 0 5px ${accent}aa)` }}>
          {!plain && <g dangerouslySetInnerHTML={{ __html: collar }} />}
          <g dangerouslySetInnerHTML={{ __html: crest }} />
        </g>

        {/* ── THE LORD'S FACE. Drawn AFTER the crest so it covers it
            when it loads and reveals it when it does not — see THE
            LORD'S FACE above. `preserveAspectRatio="xMidYMin slice"`
            crops to the top of the card, because a card's art is at the
            top and its stat line is at the bottom, and a box that
            showed the stat line would be showing the wrong half. */}
        <image
          href={cardUrl(lordId)}
          x="8" y="6" width="98" height="134"
          preserveAspectRatio="xMidYMin slice"
        />
        {/* The scrim. Keeps the belt and plate legible over any art,
            and keeps twelve very different paintings reading as one
            shelf. */}
        <rect x="8" y="6" width="98" height="134" fill={`url(#sa-scrim-${uid})`} />

        {/* ── THE BELT — the band a deck is sold banded with. It is the
            horizon of the composition: crest above, plate below, and
            the eye stops here on the way down. */}
        <rect x="8" y="112" width="98" height="15" fill={darker} opacity="0.88" />
        <rect x="8" y="112" width="98" height="1.1" fill={accent} opacity="0.85" />
        <rect x="8" y="126" width="98" height="1.1" fill={accent} opacity="0.55" />
        {!plain && (
          <g stroke={light} strokeWidth="0.9" opacity="0.45">
            {Array.from({ length: 11 }, (_, i) => (
              <path key={i} d={`M${16 + i * 8.6} 116 L${12 + i * 8.6} 123`} />
            ))}
          </g>
        )}

        {/* ── THE PLATE — the milled code strip at the foot ── */}
        <rect x="8" y="140" width="98" height="20" fill={darker} opacity="0.9" />
        <rect x="8" y="140" width="98" height="0.9" fill={`url(#sa-edge-${uid})`} />
        <text
          x="57" y="153.5" textAnchor="middle"
          fontFamily="ui-monospace, 'SF Mono', Menlo, monospace"
          fontSize="11" fontWeight="700" letterSpacing="3.4"
          fill={light} opacity="0.92"
        >{code}</text>

        {/* ── THE SHEEN — hero tiles only ── */}
        {foil && (
          <rect x="-52" y="6" width="34" height="154" fill={`url(#sa-sheen-${uid})`} opacity="0.7" transform="skewX(-14)">
            <animateTransform attributeName="transform" type="translate"
              values="0 0; 210 0; 210 0" dur="3.4s" repeatCount="indefinite" additive="sum" />
          </rect>
        )}
      </g>

      {/* ── THE MILLED EDGE + CORNER TICKS. Drawn OUTSIDE the clip so
          they sit on the object rather than inside it — the same reason
          a real card's foil edge is the last thing applied. */}
      <rect x="8" y="6" width="98" height="154" rx="5" fill="none" stroke={`url(#sa-edge-${uid})`} strokeWidth="1.6" opacity="0.8" />
      <g stroke={light} strokeWidth="1.3" opacity="0.85" fill="none">
        <path d="M13 15 L13 11 L17 11" />
        <path d="M101 15 L101 11 L97 11" />
        <path d="M13 151 L13 155 L17 155" />
        <path d="M101 151 L101 155 L97 155" />
      </g>
    </svg>
  );
}
